import { RootState, ThunkDispatch } from "core-integration/src/store";
import {
  getCurrentEvent,
  getEventsById,
} from "core-integration/src/store/events/reducers";
import {
  getClassicLeague,
  getClassicNewEntries,
  getClassicStandings,
} from "core-integration/src/store/leagues/reducers";
import { fetchClassicLeagueStandings } from "core-integration/src/store/leagues/thunks";
import { getStartedPhases } from "core-integration/src/store/phases/reducers";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import qs from "qs";
import React, { useEffect } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";
import { Box, Flex } from "rebass/styled-components";
import { integerDivide } from "../../utils/points";
import Alert from "../Alert";
import ButtonLink from "../ButtonLink";
import Copy from "../Copy";
import { InlineSelectField } from "../FieldRenderers";
import { ControlArrowEnd, ControlArrowStart } from "../icons/Arrows";
import { Main, Wrapper } from "../Layout";
import Link from "../Link";
import { Pager, PagerButton, PagerButtonNext } from "../Pager";
import SubHeading from "../SubHeading";
import Table from "../Table";
import Tooltip, { TooltipLabel } from "../Tooltip";
import Movement from "./Movement";
import StandingsHeading from "./StandingsHeading";
import StandingsRow from "./StandingsRow";

type Props = WithTranslation;

const StandingsClassic: React.FC<Props> = ({ t }) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const location = useLocation();
  const navigate = useNavigate();
  const { leagueId } = useParams();

  // Get league id from path
  const leagueNumber = parseInt(leagueId!, 10) || 0;

  // Get phase and page numbers from query string
  const query = qs.parse(location!.search, {
    ignoreQueryPrefix: true,
  });
  const pageNewEntries = parseInt(query.page_new_entries as string, 10) || 1;
  const pageStandings = parseInt(query.page_standings as string, 10) || 1;

  const now = useSelector(getCurrentEvent);
  const league = useSelector((state: RootState) =>
    getClassicLeague(state, leagueNumber),
  );
  const newEntries = useSelector((state: RootState) =>
    getClassicNewEntries(state, leagueNumber, pageNewEntries),
  );
  const phase = parseInt(query.phase as string, 10) || 1;
  const phases = useSelector(getStartedPhases);
  const player = useSelector(getPlayerData);
  const eventsById = useSelector(getEventsById);
  const standings = useSelector((state: RootState) =>
    getClassicStandings(state, leagueNumber, phase, pageStandings),
  );

  useEffect(() => {
    if (leagueNumber) {
      dispatch(
        fetchClassicLeagueStandings(
          leagueNumber,
          pageNewEntries,
          pageStandings,
          phase,
        ),
      );
    }
  }, [dispatch, leagueNumber, phase, pageNewEntries, pageStandings]);

  const buildLink = (
    phase: number,
    pageNewEntries: number,
    pageStandings: number,
  ) => {
    return `/leagues/${
      league!.id
    }/standings/c?phase=${phase}&page_new_entries=${pageNewEntries}&page_standings=${pageStandings}`;
  };

  const handleChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    navigate(buildLink(parseInt(e.currentTarget.value, 10), 1, 1));

  if (!league) {
    return null;
  }

  const started = now && league.start_event <= now.id;
  const startEvent = eventsById[league.start_event];
  return (
    <>
      <Wrapper>
        <Main>
          <Copy>
            <StandingsHeading
              type="classic"
              codePrivacy={league.code_privacy}
              leagueTitle={league.name}
              leagueNumber={leagueNumber}
            />
            {league.start_event > 1 && startEvent && (
              <Box m={2}>
                <Alert type="info">
                  {started
                    ? t(
                        "myLeagues.standingsClassic.alertStarted",
                        "League scoring started in {{startEvent}}",
                        { startEvent: startEvent.name },
                      )
                    : t(
                        "myLeagues.standingsClassic.alertWillStart",
                        "League scoring will start in {{startEvent}}",
                        { startEvent: startEvent.name },
                      )}
                </Alert>
              </Box>
            )}

            <Box mb={4}>
              <form>
                <InlineSelectField
                  id="selectPhase"
                  name="selectPhase"
                  label={t("standingsClassic.selectPhase", "Select phase")}
                  onChange={handleChange}
                  value={phase}
                >
                  {phases.map((p) => (
                    <option
                      value={p.id}
                      key={p.id}
                      aria-selected={phase === p.id}
                    >
                      {p.name}
                    </option>
                  ))}
                </InlineSelectField>
              </form>
            </Box>

            <Table>
              <thead>
                <tr>
                  <th>{t("standingsClassic.rank", "Rank")}</th>
                  <th>
                    {t(
                      "standingsClassic.teamAndManagern",
                      "Team & General Manager",
                    )}
                  </th>
                  <th>
                    <Tooltip
                      content={t(
                        "standingsClassic.roundPointsn",
                        "Gameday fantasy points",
                      )}
                    >
                      <TooltipLabel>GD</TooltipLabel>
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip
                      content={t(
                        "standingsClassic.totalPoints",
                        "Total points",
                      )}
                    >
                      <TooltipLabel>TOT</TooltipLabel>
                    </Tooltip>
                  </th>
                </tr>
              </thead>
              <tbody>
                {standings &&
                  standings.results.map((ls) => (
                    <StandingsRow
                      mine={player !== null && player.entry === ls.entry}
                      key={ls.entry}
                    >
                      <td>
                        <Flex alignItems="center">
                          <Box mr={1}>{ls.rank}</Box>
                          <Movement lastRank={ls.last_rank} rank={ls.rank} />
                        </Flex>
                      </td>
                      <td>
                        {now ? (
                          <Link to={`/entry/${ls.entry}/event/${now.id}`}>
                            <strong>{ls.entry_name}</strong>
                          </Link>
                        ) : (
                          <strong>{ls.entry_name}</strong>
                        )}
                        <br />
                        {ls.player_name}
                      </td>
                      <td>{integerDivide(ls.event_total, 10)}</td>
                      <td>{integerDivide(ls.total, 10)}</td>
                    </StandingsRow>
                  ))}
              </tbody>
            </Table>
            {standings && (
              <Pager>
                <PagerButton>
                  {pageStandings > 1 && (
                    <ButtonLink
                      to={buildLink(phase, pageNewEntries, pageStandings - 1)}
                      variant="secondary"
                    >
                      <ControlArrowStart />
                      {t("standingsClassic.previous", "Previous")}
                    </ButtonLink>
                  )}
                </PagerButton>
                <PagerButtonNext>
                  {standings.has_next && (
                    <ButtonLink
                      to={buildLink(phase, pageNewEntries, pageStandings + 1)}
                      variant="secondary"
                    >
                      {t("standingsClassic.next", "Next")}
                      <ControlArrowEnd />
                    </ButtonLink>
                  )}
                </PagerButtonNext>
              </Pager>
            )}
            {newEntries && newEntries.results.length > 0 && (
              <>
                <SubHeading>
                  {t("standingsClassic.newEntries", "New Entries")}
                </SubHeading>
                <Table>
                  <thead>
                    <tr>
                      <th>{t("standingsClassic.team", "Team")}</th>
                      <th>
                        {t("standingsClassic.managern", "General Manager")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {newEntries.results.map((ne) => (
                      <tr key={ne.entry}>
                        <td>{ne.entry_name}</td>
                        <td>
                          <Link to={`/entry/${ne.entry}/history`}>
                            {ne.player_first_name} {ne.player_last_name}
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <Pager>
                  <PagerButton>
                    {pageNewEntries > 1 && (
                      <ButtonLink
                        to={buildLink(phase, pageNewEntries - 1, pageStandings)}
                        variant="secondary"
                      >
                        <ControlArrowStart />
                        {t("standingsClassic.previous", "Previous")}
                      </ButtonLink>
                    )}
                  </PagerButton>
                  <PagerButtonNext>
                    {newEntries.has_next && (
                      <ButtonLink
                        to={buildLink(phase, pageNewEntries + 1, pageStandings)}
                        variant="secondary"
                      >
                        {t("standingsClassic.next", "Next")}
                        <ControlArrowEnd />
                      </ButtonLink>
                    )}
                  </PagerButtonNext>
                </Pager>
              </>
            )}
          </Copy>
        </Main>
      </Wrapper>
    </>
  );
};

export default withTranslation()(StandingsClassic);
